/**
 * Notes & annotations ancrées (par document).
 *
 * @remarks
 * Découpé du legacy. Les annotations sont ancrées au DOM via `blockId` +
 * offsets char (cf. `evaluation-workspace.ts`).
 *
 * Les bookmarks ne vivent plus ici : ils sont passés en data serveur (TanStack
 * Query via `useAttemptBookmarks`), cf. règle CLAUDE.md §1 « jamais les données
 * serveur dans Zustand ».
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { uid } from '../lib/status-helpers';
import type { Annotation, DocumentNotes } from '../types';

interface NotesState {
  documentNotes: Record<string, DocumentNotes>;

  setDocumentFreeText: (documentId: string, text: string) => void;
  addAnnotation: (documentId: string, annotation: Omit<Annotation, 'id' | 'createdAt'>) => void;
  setDocumentNoteKey: (documentId: string, noteKey: string) => void;
  deleteAnnotation: (documentId: string, annotationId: string) => void;
  updateAnnotation: (documentId: string, annotationId: string, note: string) => void;
}

const ensureNotes = (state: NotesState, documentId: string): DocumentNotes =>
  state.documentNotes[documentId] ?? {
    documentId,
    freeText: '',
    annotations: [],
  };

export const useNotesStore = create<NotesState>()(
  devtools(
    persist(
      (set) => ({
        documentNotes: {},

        setDocumentFreeText: (documentId, text) => {
          set((state) => ({
            documentNotes: {
              ...state.documentNotes,
              [documentId]: { ...ensureNotes(state, documentId), freeText: text },
            },
          }));
        },

        addAnnotation: (documentId, annotation) => {
          const id = uid('ann');
          set((state) => {
            const existing = ensureNotes(state, documentId);
            return {
              documentNotes: {
                ...state.documentNotes,
                [documentId]: {
                  ...existing,
                  annotations: [
                    ...existing.annotations,
                    { ...annotation, id, createdAt: Date.now() },
                  ],
                },
              },
            };
          });
        },

        setDocumentNoteKey: (documentId, noteKey) => {
          set((state) => ({
            documentNotes: {
              ...state.documentNotes,
              [documentId]: { ...ensureNotes(state, documentId), remoteNoteKey: noteKey },
            },
          }));
        },

        deleteAnnotation: (documentId, annotationId) => {
          set((state) => {
            const existing = state.documentNotes[documentId];
            if (!existing) return {};
            return {
              documentNotes: {
                ...state.documentNotes,
                [documentId]: {
                  ...existing,
                  annotations: existing.annotations.filter((a) => a.id !== annotationId),
                },
              },
            };
          });
        },

        updateAnnotation: (documentId, annotationId, note) => {
          set((state) => {
            const existing = state.documentNotes[documentId];
            if (!existing) return {};
            return {
              documentNotes: {
                ...state.documentNotes,
                [documentId]: {
                  ...existing,
                  annotations: existing.annotations.map((a) =>
                    a.id === annotationId ? { ...a, note, updatedAt: Date.now() } : a,
                  ),
                },
              },
            };
          });
        },
      }),
      {
        name: 'saas-front-v2:tech-tests-notes',
        version: 2,
        partialize: (state) => ({
          documentNotes: state.documentNotes,
        }),
        /**
         * Migration policy : si la shape de `Annotation` change (ex: ajout de
         * field obligatoire), bumper `version` et adapter. Payload corrompu →
         * map vide (l'utilisateur perd ses notes locales mais l'app reste
         * utilisable, pas de crash sur `.map()`).
         *
         * v1 → v2 : les bookmarks ont quitté le store (passés en data serveur).
         * On ne garde que `documentNotes` ; tout `bookmarks` persisté est ignoré.
         */
        migrate: (persistedState: unknown) => {
          const fallback = { documentNotes: {} };
          try {
            if (
              persistedState !== null &&
              typeof persistedState === 'object' &&
              'documentNotes' in persistedState
            ) {
              return {
                documentNotes: (persistedState as Pick<NotesState, 'documentNotes'>).documentNotes,
              };
            }
            return fallback;
          } catch {
            return fallback;
          }
        },
      },
    ),
    { name: 'notes', enabled: import.meta.env.DEV },
  ),
);
